import { Box, Button } from '@factorialco/deprecated-design-system'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Group, useUpdate } from 'modules/expenses/hooks/useGroup'
import { canEditGroup } from 'modules/expenses/lib/groups/availableActions'

import { Form } from './_Form'
import { EditGroupFormValues, GroupFile } from './types'

interface GroupDetailProps {
  group: Group
  onClose: () => void
}

const parseUploadedFiles = (files: Group['files']): GroupFile[] =>
  files.map(({ id, filename, url, downloadUrl, byteSize }) => ({
    id,
    name: filename,
    size: byteSize,
    url: url ?? undefined,
    downloadUrl: downloadUrl ?? undefined,
  }))

export const EditGroup = ({ group, onClose }: GroupDetailProps) => {
  const { employee, company } = useCurrentSession()
  const { update } = useUpdate()

  const canEdit = group && canEditGroup(group, employee?.id)

  const form = useForm<EditGroupFormValues>({
    enableReinitialize: true,
    defaultValues: {
      name: group?.name ?? '',
      description: group?.description ?? undefined,
      files: parseUploadedFiles(group?.files ?? []),
      filesIdToRemove: [],
    },
    validate: (data) => {
      const errors: FormErrors<EditGroupFormValues> = {}

      if (!data.name.trim()) {
        errors.name = i18n.t('expenses_v2.forms.errors.required')
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await update({
        ...data,
        id: group.id,
        files: data.files.filter(({ id }) => !id),
        companyId: company.id,
      })

      const handled = handleResult(result, ({ name, description }) => {
        form.setErrors({ name, description })
      })

      if (handled.success) {
        const group = handled.data.expensableGroup
        if (!group) return handled

        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses_v2.group_edit.updated_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Sidepanel
      open
      locked
      width="wide"
      title="Group information"
      footer={
        canEdit && (
          <Box gap="s8" flexDirection="row">
            <Button type="secondary" onClick={onClose} disabled={form.isSubmitting}>
              {i18n.t('expenses_v2.buttons.cancel')}
            </Button>
            <Button onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
              {i18n.t('expenses_v2.buttons.save')}
            </Button>
          </Box>
        )
      }
      closeLabel={i18n.t('common.close')}
      onClose={onClose}
    >
      <Form group={group} form={form} canEdit={Boolean(canEdit)} />
    </Sidepanel>
  )
}
